/* global google, propel */

import { __ } from '@wordpress/i18n';
import sanitizeHTML from 'propel/sanitizeHTML';
import Filters from '../../components/filters/script';
import throttle from 'lodash/throttle';
import getRemBase from 'propel/getRemBase';

const blocks = document.querySelectorAll('.block-filter-locations');
const header = document.querySelector('.main-header');

if (blocks.length > 0) {
	blocks.forEach((block) => {
		const postsWrapper = block.querySelector(
			'.block-filter__posts-wrapper'
		);

		if (postsWrapper) {
			new Filters(block, postsWrapper);
		}
	});
}

/**
 * LocationMap class to handle map-related functionalities.
 */
class LocationMap {
	/**
	 * Initializes the Filters class with provided block and postsWrapper elements.
	 *
	 * @param {HTMLElement} block                 - The root element of the Filter-Locations block.
	 * @param {Object}      Map                   - The Google Maps Map class.
	 * @param {Object}      InfoWindow            - The Google Maps InfoWindow class.
	 * @param {Object}      AdvancedMarkerElement - The Google Maps AdvancedMarkerElement class.
	 * @param {Object}      PinElement            - The Google Maps PinElement class.
	 * @param {Object}      RenderingType         - The Google Maps RenderingType enum.
	 * @return {boolean} Returns false if the block element is falsy.
	 */
	constructor(
		block,
		Map,
		InfoWindow,
		AdvancedMarkerElement,
		PinElement,
		RenderingType
	) {
		this.parser = new DOMParser();
		this.mapElement = block.querySelector('.block-filter-locations__map');
		this.block = block;
		this.blockContainer = block.querySelector(
			'.block-filter-locations__container'
		);
		this.postsContainer = block.querySelector(
			'.block-filter__posts-container'
		);
		this.postsContainerWrapper = block.querySelector(
			'.block-filter-locations__posts-container'
		);
		this.AdvancedMarkerElement = AdvancedMarkerElement;
		this.PinElement = PinElement;
		this.latInput = block.querySelector(
			'.block-location-search-field__lat-input'
		);
		this.lngInput = block.querySelector(
			'.block-location-search-field__lng-input'
		);
		this.searchInput = block.querySelector(
			'.block-location-search-field__input'
		);
		this.form = block.querySelector('.block-location-search-field__form');
		this.filterContainers = block.querySelectorAll('.filter-container');
		this.filterSelected = block.querySelector('.filter-selected');
		this.mapMoved = false;
		this.movedCenter = null;
		this.locationChangedEvent = new CustomEvent(
			'filter-locations-user-location-changed',
			{
				detail: {
					block: this.block,
				},
			}
		);
		this.locationsLoadedEvent = new CustomEvent(
			'propel-filter-locations-loaded',
			{
				detail: {
					block: this.block,
				},
			}
		);
		this.jsonSettings = JSON.parse(this.block.dataset.jsonSettings);
		this.displayAllLocations =
			this.jsonSettings.displayAllLocations ?? null;

		if (
			!this.mapElement ||
			!this.postsContainer ||
			!this.postsContainerWrapper
		) {
			return false;
		}

		const options = {
			center: {
				lat: 39.8283,
				lng: -98.5795,
			},
			zoom: 4,
			mapTypeControl: false,
			fullscreenControl: false,
			streetViewControl: false,
			scaleControl: false,
			zoomControl: true,
			mapId: '6ab47e49eb38c78cfb82aa3c',
			renderingType: RenderingType.VECTOR,
			allMarkers: {},
		};

		this.map = new Map(this.mapElement, options);
		this.markers = {};
		this.infoWindow = new InfoWindow({
			pixelOffset: new google.maps.Size(0, -4),
		});

		google.maps.event.addListener(this.infoWindow, 'domready', () => {
			const iwOuter = document.querySelector('.gm-style-iw');

			if (iwOuter) {
				iwOuter.addEventListener('click', (e) => {
					if (e.target.tagName && 'SUMMARY' === e.target.tagName) {
						this.infoWindow.open({
							anchor: this.infoWindow.anchor,
							map: this.map,
							shouldFocus: false,
						});
					}
				});
			}
		});

		this.addMapMarkers();

		if (!this.displayAllLocations) {
			this.addMapSearchButton();
		}

		window.addEventListener('propel-filters-toggled', () => {
			this.postsContainerWrapper.scrollTop = 0;
		});

		window.addEventListener('propel-filters-render-posts', (e) => {
			this.infoWindow.close();
			this.addMapMarkers();

			const scrollToBlockTop = e?.detail?.scrollToBlockTop || false;

			if (window.innerWidth >= 992 && scrollToBlockTop) {
				this.scrollToMap();
			}

			this.setCssCustomProperties();

			if (this.searchAreaButton) {
				this.searchAreaButton.style.display = 'none';
				this.mapMoved = false;
			}

			window.dispatchEvent(this.locationsLoadedEvent);
		});

		window.addEventListener('propel-filters-render-no-results', () => {
			Object.values(this.markers).forEach((marker) => {
				marker.setMap(null);
			});
		});

		window.addEventListener('propel-current-location-set', (e) => {
			if (!e.detail) {
				return;
			}

			const { locationId } = e.detail;

			this.updateFavoriteMarker(locationId ?? '');
		});

		this.setCssCustomProperties();

		window.addEventListener(
			'resize',
			throttle(this.setCssCustomProperties, 100)
		);

		this.map.addListener('dragend', () => {
			this.mapMoved = true;
		});
	}

	addMapMarkers() {
		Object.values(this.markers).forEach((marker) => {
			marker.setMap(null);
		});

		this.addUserMarker();

		const locationCards =
			this.postsContainer.querySelectorAll('.location-card');

		const visibleMarkers = [];

		locationCards.forEach((locationCard, index) => {
			const marker = this.addMarker(locationCard, index);

			if (!marker) {
				return;
			}

			visibleMarkers.push(marker);

			if (!marker.zIndex) {
				marker.zIndex = 10000 - index;
			}
		});

		this.centerMap();
	}

	addUserMarker() {
		if (!this.latInput || !this.lngInput) {
			return;
		}

		const lat = this.latInput.value;
		const lng = this.lngInput.value;

		if (!lat || !lng) {
			return;
		}

		const latLng = new google.maps.LatLng(lat, lng);

		if (this.markers.user) {
			this.markers.user.setMap(this.map);
			this.markers.user.position = latLng;

			return;
		}

		const markerContentElement = this.getMarkerContentElement(
			'',
			'user',
			'block-filter-locations__user-marker'
		);

		const markerSettings = {
			position: latLng,
			map: this.map,
			title: __('Your Location', 'propel'),
			content: markerContentElement,
			zIndex: 10001,
			gmpDraggable: true,
		};
		this.markers.user = new this.AdvancedMarkerElement(markerSettings);

		this.markers.user.addListener('dragend', (e) => {
			this.updateInputFieldsWithLatLng(e.latLng);

			window.dispatchEvent(this.locationChangedEvent);
		});
	}

	addMarker(locationCard, index) {
		if (
			!locationCard?.dataset?.id ||
			!locationCard?.dataset?.lat ||
			!locationCard?.dataset?.lng
		) {
			return;
		}

		const locationId = locationCard.dataset.id;

		if (this.markers[locationId]) {
			this.markers[locationId].setMap(this.map);
			this.markers[locationId].locationOrder = index;

			if (locationCard?.dataset?.distance) {
				this.markers[locationId].distance = parseFloat(
					locationCard.dataset.distance
				);
			}

			google.maps.event.clearListeners(this.markers[locationId], 'click');

			this.markers[locationId].addListener(
				'click',
				this.openMarker.bind(
					this,
					this.markers[locationId],
					locationCard
				)
			);

			return this.markers[locationId];
		}

		const latLng = new google.maps.LatLng(
			locationCard.dataset.lat,
			locationCard.dataset.lng
		);

		const title = locationCard?.dataset?.title || '';
		const permalink = locationCard?.dataset?.permalink || '';
		const address = locationCard?.dataset?.address || '';
		const hoursSummary = locationCard.querySelector(
			'.location-hours__summary'
		);
		let markerIcon = locationCard.querySelector(
			'.location-meta__set-location-button--selected'
		)
			? 'selected'
			: '';

		if (
			locationCard.querySelector(
				'.location-card__admin-details--franchise'
			)
		) {
			markerIcon = 'franchise';
		}

		let markerHtml = `<div class="component-map-marker" aria-labelledby="location-marker-title"><a id="location-marker-title" class="component-map-marker__title" href="${permalink}">${title}</a>`;

		if (address) {
			markerHtml += `<div class="component-map-marker__address">${address}</div>`;
		}

		if (hoursSummary) {
			markerHtml += `<div class="component-map-marker__hours">${hoursSummary.innerHTML}</div>`;
		}

		markerHtml += '</div>';

		const markerContentElement = this.getMarkerContentElement(
			markerHtml,
			markerIcon,
			markerIcon
				? [
						'component-map-marker__icon',
						'component-map-marker__icon--selected',
					]
				: ['component-map-marker__icon']
		);

		const locationTitle = locationCard.getAttribute('data-title');

		const markerSettings = {
			position: latLng,
			map: this.map,
			title: `${__('View details for', 'propel')} ${locationTitle}`,
			content: markerContentElement,
		};

		const marker = new this.AdvancedMarkerElement(markerSettings);

		if ('franchise' === markerIcon) {
			marker.zIndex = 1;
		} else if ('selected' === markerIcon) {
			marker.zIndex = 10002;
		}

		if (locationCard?.dataset?.distance) {
			marker.distance = parseFloat(locationCard.dataset.distance);
		}

		marker.locationOrder = index;

		this.markers[locationId] = marker;

		marker.addListener(
			'click',
			this.openMarker.bind(this, marker, locationCard)
		);

		return marker;
	}

	getMarkerContentElement(markerHtml = '', markerIcon = '', classes = []) {
		const markerContentElement = document.createElement('div');
		markerContentElement.setAttribute(
			'data-content',
			sanitizeHTML(markerHtml)
		);
		markerContentElement.appendChild(this.getMarkerIconElement(markerIcon));

		if (typeof classes === 'string') {
			classes = [classes];
		}

		classes.forEach((className) => {
			markerContentElement.classList.add(className);
		});

		return markerContentElement;
	}

	getMarkerIconElement(markerIcon = '') {
		if ('selected' === markerIcon) {
			markerIcon = `<svg width="31" height="38" fill="none" version="1.1" viewBox="0 0 31 38" xmlns="http://www.w3.org/2000/svg"><g transform="translate(-8.2617 -4.4531)" filter="url(#filter0_d_33499_169926)"><path d="m23.762 5.7102c7.9496 0 14.5 6.0177 14.5 13.6 0 3.4458-1.6347 7.3163-3.6767 10.841-2.0601 3.5558-4.62 6.9012-6.6377 9.332-2.2047 2.6559-6.1665 2.6559-8.3711 0-2.0178-2.4308-4.5776-5.7762-6.6377-9.332-2.042-3.5246-3.6768-7.3951-3.6768-10.841 2.2e-4 -7.5819 6.5504-13.6 14.5-13.6z" fill="var(--button-primary-light-default, #777)" stroke="#fff" stroke-width="2"/><path d="m23.762 27.966-1.0875-0.99c-3.8625-3.5025-6.4125-5.8125-6.4125-8.6475 0-2.31 1.815-4.125 4.125-4.125 1.305 0 2.5575 0.6075 3.375 1.5675 0.8175-0.96 2.07-1.5675 3.375-1.5675 2.31 0 4.125 1.815 4.125 4.125 0 2.835-2.55 5.145-6.4125 8.655z" fill="#fff"/></g><defs><filter id="filter0_d_33499_169926" x="-.23828" y="-.046875" width="48" height="55" color-interpolation-filters="sRGB" filterUnits="userSpaceOnUse"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/><feOffset dy="4"/><feGaussianBlur stdDeviation="4"/><feComposite in2="hardAlpha" operator="out"/><feColorMatrix values="0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0.06 0"/><feBlend in2="BackgroundImageFix" result="effect1_dropShadow_33499_169926"/><feBlend in="SourceGraphic" in2="effect1_dropShadow_33499_169926" result="shape"/></filter></defs></svg>`;
		} else if ('user' === markerIcon) {
			markerIcon = `<svg width="36" height="36" fill="none" version="1.1" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><g transform="translate(-8,-4)" filter="url(#filter0_d_35548_42133)"><circle cx="26" cy="22" r="17" fill="#000" stroke="#fff" stroke-width="2"/><path d="m33.875 20.312h-0.7594l-1.9533-4.3945c-0.0884-0.1989-0.2325-0.3678-0.415-0.4863-0.1824-0.1186-0.3953-0.1817-0.6129-0.1817h-8.2688c-0.2176 0-0.4305 0.0631-0.6129 0.1817-0.1825 0.1185-0.3266 0.2874-0.415 0.4863l-1.9533 4.3945h-0.7594c-0.1492 0-0.2923 0.0593-0.3977 0.1648-0.1055 0.1054-0.1648 0.2485-0.1648 0.3977s0.0593 0.2923 0.1648 0.3977c0.1054 0.1055 0.2485 0.1648 0.3977 0.1648h0.5625v5.625c0 0.2984 0.1185 0.5845 0.3295 0.7955s0.4971 0.3295 0.7955 0.3295h1.6875c0.2984 0 0.5845-0.1185 0.7955-0.3295s0.3295-0.4971 0.3295-0.7955v-0.5625h6.75v0.5625c0 0.2984 0.1185 0.5845 0.3295 0.7955s0.4971 0.3295 0.7955 0.3295h1.6875c0.2984 0 0.5845-0.1185 0.7955-0.3295s0.3295-0.4971 0.3295-0.7955v-5.625h0.5625c0.1492 0 0.2923-0.0593 0.3977-0.1648 0.1055-0.1054 0.1648-0.2485 0.1648-0.3977s-0.0593-0.2923-0.1648-0.3977c-0.1054-0.1055-0.2485-0.1648-0.3977-0.1648zm-11.25 3.375h-1.6875c-0.1492 0-0.2923-0.0593-0.3977-0.1648-0.1055-0.1054-0.1648-0.2485-0.1648-0.3977s0.0593-0.2923 0.1648-0.3977c0.1054-0.1055 0.2485-0.1648 0.3977-0.1648h1.6875c0.1492 0 0.2923 0.0593 0.3977 0.1648 0.1055 0.1054 0.1648 0.2485 0.1648 0.3977s-0.0593 0.2923-0.1648 0.3977c-0.1054 0.1055-0.2485 0.1648-0.3977 0.1648zm8.4375 0h-1.6875c-0.1492 0-0.2923-0.0593-0.3977-0.1648-0.1055-0.1054-0.1648-0.2485-0.1648-0.3977s0.0593-0.2923 0.1648-0.3977c0.1054-0.1055 0.2485-0.1648 0.3977-0.1648h1.6875c0.1492 0 0.2923 0.0593 0.3977 0.1648 0.1055 0.1054 0.1648 0.2485 0.1648 0.3977s-0.0593 0.2923-0.1648 0.3977c-0.1054 0.1055-0.2485 0.1648-0.3977 0.1648zm-10.947-3.375 1.7501-3.9375h8.2688l1.7501 3.9375z" fill="#fff"/></g><defs><filter id="filter0_d_35548_42133" x="0" y="0" width="52" height="52" color-interpolation-filters="sRGB" filterUnits="userSpaceOnUse"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/><feOffset dy="4"/><feGaussianBlur stdDeviation="4"/><feComposite in2="hardAlpha" operator="out"/><feColorMatrix values="0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0.06 0"/><feBlend in2="BackgroundImageFix" result="effect1_dropShadow_35548_42133"/><feBlend in="SourceGraphic" in2="effect1_dropShadow_35548_42133" result="shape"/></filter></defs></svg>`;
		} else if ('franchise' === markerIcon) {
			markerIcon = `<svg width="31" height="38" fill="none" version="1.1" viewBox="0 0 31 38" xmlns="http://www.w3.org/2000/svg"><g transform="translate(-8.5,-4.5)" filter="url(#filter0_d_33499_168150)"><path d="m24 5.7571c7.9496 0 14.5 6.0176 14.5 13.6 0 3.4458-1.6348 7.3162-3.6768 10.841-2.0601 3.5559-4.6199 6.9013-6.6377 9.3321-2.2046 2.6559-6.1664 2.6559-8.371 0-2.0178-2.4308-4.5776-5.7762-6.6377-9.3321-2.042-3.5246-3.6768-7.395-3.6768-10.841 2.3e-4 -7.582 6.5504-13.6 14.5-13.6zm0 10.1c-2.1751 0-3.8211 1.6323-3.8213 3.5 0 1.8679 1.6461 3.5 3.8213 3.5s3.8213-1.6321 3.8213-3.5c-2e-4 -1.8677-1.6462-3.5-3.8213-3.5z" fill="var(--closest-location, #777)" stroke="#fff" stroke-width="2"/></g><defs><filter id="filter0_d_33499_168150" x="0" y="0" width="48" height="55" color-interpolation-filters="sRGB" filterUnits="userSpaceOnUse"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/><feOffset dy="4"/><feGaussianBlur stdDeviation="4"/><feComposite in2="hardAlpha" operator="out"/><feColorMatrix values="0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0.06 0"/><feBlend in2="BackgroundImageFix" result="effect1_dropShadow_33499_168150"/><feBlend in="SourceGraphic" in2="effect1_dropShadow_33499_168150" result="shape"/></filter></defs></svg>`;
		} else if (propel?.customMarkerIcon) {
			markerIcon = propel.customMarkerIcon;
		} else {
			markerIcon = `<svg width="31" height="38" fill="none" version="1.1" viewBox="0 0 31 38" xmlns="http://www.w3.org/2000/svg"><g transform="translate(-8.5,-4.5)" filter="url(#filter0_d_33499_168150)"><path d="m24 5.7571c7.9496 0 14.5 6.0176 14.5 13.6 0 3.4458-1.6348 7.3162-3.6768 10.841-2.0601 3.5559-4.6199 6.9013-6.6377 9.3321-2.2046 2.6559-6.1664 2.6559-8.371 0-2.0178-2.4308-4.5776-5.7762-6.6377-9.3321-2.042-3.5246-3.6768-7.395-3.6768-10.841 2.3e-4 -7.582 6.5504-13.6 14.5-13.6zm0 10.1c-2.1751 0-3.8211 1.6323-3.8213 3.5 0 1.8679 1.6461 3.5 3.8213 3.5s3.8213-1.6321 3.8213-3.5c-2e-4 -1.8677-1.6462-3.5-3.8213-3.5z" fill="var(--default-location, #777)" stroke="#fff" stroke-width="2"/></g><defs><filter id="filter0_d_33499_168150" x="0" y="0" width="48" height="55" color-interpolation-filters="sRGB" filterUnits="userSpaceOnUse"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/><feOffset dy="4"/><feGaussianBlur stdDeviation="4"/><feComposite in2="hardAlpha" operator="out"/><feColorMatrix values="0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0.06 0"/><feBlend in2="BackgroundImageFix" result="effect1_dropShadow_33499_168150"/><feBlend in="SourceGraphic" in2="effect1_dropShadow_33499_168150" result="shape"/></filter></defs></svg>`;
		}

		return this.parser.parseFromString(markerIcon, 'image/svg+xml')
			.documentElement;
	}

	openMarker(marker, locationCard) {
		this.infoWindow.close();
		this.infoWindow.setContent(marker.content.getAttribute('data-content'));
		this.infoWindow.open({
			anchor: marker,
			map: this.map,
			shouldFocus: false,
		});

		if (window.innerWidth >= 992) {
			this.postsContainerWrapper.scrollTop = locationCard.offsetTop;

			this.scrollToMap();
		}
	}

	centerMap() {
		const bounds = new google.maps.LatLngBounds();
		let markerCount = 0;

		const sortedMarkers = Object.values(this.markers).sort((a, b) => {
			return a.locationOrder - b.locationOrder;
		});

		sortedMarkers.forEach((marker) => {
			if (
				this.displayAllLocations &&
				marker?.distance > 50 &&
				markerCount >= 1
			) {
				return;
			}

			if (!marker.map || !marker.position) {
				return;
			}

			bounds.extend(marker.position);
			markerCount++;
		});

		if (markerCount === 1) {
			this.map.setCenter(bounds.getCenter());
			this.map.setZoom(16);
		} else if (!bounds.isEmpty()) {
			this.map.fitBounds(bounds, 60);
		}

		this.mapMoved = false;
	}

	scrollToMap() {
		if (!this.blockContainer || !header) {
			return;
		}

		const headerRect = header.getBoundingClientRect();
		const blockRect = this.blockContainer.getBoundingClientRect();
		const scrollY = window.scrollY + blockRect.top - headerRect.bottom - 20;

		window.scrollTo({
			top: scrollY,
			behavior: 'smooth',
		});
	}

	setCssCustomProperties() {
		if (this.filterSelected) {
			const topFiltersHeight =
				this.filterSelected.getBoundingClientRect().bottom -
				this.filterSelected.parentElement.getBoundingClientRect().top;

			this.block.style.setProperty(
				'--topFiltersHeight',
				`${topFiltersHeight / getRemBase()}rem`
			);
		}

		if (this.filterContainers) {
			this.filterContainers.forEach((filterContainer) => {
				const filterSize =
					this.postsContainerWrapper.getBoundingClientRect().bottom -
					filterContainer.getBoundingClientRect().bottom;

				filterContainer.style.setProperty(
					'--filterSize',
					`${filterSize / getRemBase()}rem`
				);
			});
		}
	}

	updateInputFieldsWithLatLng(position) {
		if (this.latInput) {
			this.latInput.value = position.lat();
		}

		if (this.lngInput) {
			this.lngInput.value = position.lng();
		}

		const gmpElements = this.form.querySelectorAll(
			'.block-location-search-field gmp-place-autocomplete'
		);

		gmpElements.forEach((gmpElement) => {
			const input = gmpElement?.shadowRoot?.querySelector('input');

			if (input) {
				input.value = `${position.lat()}, ${position.lng()}`;
				this.form.setAttribute(
					'data-search',
					`${position.lat()}, ${position.lng()}`
				);
			}
		});
	}

	addMapSearchButton() {
		const searchAreaButton = document.createElement('button');
		searchAreaButton.textContent = __('Search this area', 'propel');
		searchAreaButton.classList.add(
			'block-filter-locations__search-area-button'
		);
		searchAreaButton.style.display = 'none';

		this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(
			searchAreaButton
		);

		this.map.addListener('idle', () => {
			if (this.mapMoved) {
				searchAreaButton.style.display = 'block';
			}
		});

		searchAreaButton.addEventListener('click', () => {
			this.searchInput.value = '';
			this.form.setAttribute('data-search', '');
			this.movedCenter = this.map.getCenter();
			this.updateInputFieldsWithLatLng(this.movedCenter);
			window.dispatchEvent(this.locationChangedEvent);

			searchAreaButton.style.display = 'none';
			this.mapMoved = false;
		});

		this.searchAreaButton = searchAreaButton;
	}

	updateFavoriteMarker(locationId) {
		Object.entries(this.markers).forEach(([key, marker]) => {
			if ('user' === key) {
				return;
			}

			if (locationId === key) {
				marker.content.innerHTML = '';
				marker.content.append(this.getMarkerIconElement('selected'));
				marker.content.classList.add(
					'component-map-marker__icon--selected'
				);
			} else if (
				marker.content.classList.contains(
					'component-map-marker__icon--selected'
				)
			) {
				marker.content.innerHTML = '';
				marker.content.append(this.getMarkerIconElement());
				marker.content.classList.remove(
					'component-map-marker__icon--selected'
				);
			}
		});
	}
}

const initLocationFinder = async () => {
	// eslint-disable-next-line no-unused-vars
	const { Map, InfoWindow, RenderingType } =
		await google.maps.importLibrary('maps');

	// eslint-disable-next-line no-unused-vars
	const { AdvancedMarkerElement, PinElement } =
		await google.maps.importLibrary('marker');

	const mapBlocks = document.querySelectorAll('.block-filter-locations');

	mapBlocks.forEach((mapBlock) => {
		new LocationMap(
			mapBlock,
			Map,
			InfoWindow,
			AdvancedMarkerElement,
			PinElement,
			RenderingType
		);
	});
};

if (window?.google?.maps) {
	initLocationFinder();
} else {
	window.addEventListener('google-maps-loaded', initLocationFinder);
}
